import { useEffect, useRef, useState } from "react";
import axios from "axios";
import useAuthStore from "../../services/authStore";
import "./MyRequest.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import pendingIcon from "../../assets/icons/warning.png";
import approvedIcon from "../../assets/icons/checkmark.png";
import rejectedIcon from "../../assets/icons/close.png";
import attachmentIcon from "../../assets/icons/attach.png";
import { LuCornerUpLeft } from "react-icons/lu";
import backIcon from "../../assets/icons/back.png";
import { Eye, Download } from "lucide-react";
import { toastError, toastSuccess } from "../../services/toaster";
import pdfIcon from "../../assets/icons/pdf.png";
import { FaSpinner } from "react-icons/fa";
import jsPDF from "jspdf";
import { toWords } from "number-to-words";
import autoTable from "jspdf-autotable";
import editIcon from "../../assets/icons/edit.png";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL;

export default function MyRequest() {
  const [loading, setLoading] = useState(false);
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user_id);
  const companyId = useAuthStore((s) => s.companyId);
  const { status } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDetail, setShowDetail] = useState(false);
  const setRequestCounts = useAuthStore((s) => s.setRequestCounts);
  const setAuthCounts = useAuthStore((s) => s.setAuthCounts);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const searchFilters = useAuthStore((s) => s.searchFilters);

  const [selected, setSelected] = useState(null);
  const users = useAuthStore((s) => s.users);
  const searchMatchesRef = useRef([]);
  const activeSearchMatchRef = useRef(-1);
  const organizationLevels = useAuthStore((g) => g.organizationLevels);
  const getGradeDetails = (gradeId) => {
    return organizationLevels.find(
      (item) => String(item.id) === String(gradeId),
    );
  };
  const downloadExpensePdf = (company) => {
    if (!selected) return;

    const user = getUserDetails(selected.user_id);

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const leftMargin = 16;
    const rightMargin = 10;
    const pageWidth = 210;
    const pageHeight = 297;
    const bottomMargin = 10;

    let y = 12;

    // =========================
    // COMPANY HEADER
    // =========================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);

    doc.text(company?.company_name || "COMPANY", leftMargin, y);

    y += 6;

    doc.setFontSize(10);
    doc.text("Expenses Reimbursement Form", leftMargin, y);

    doc.setFont("helvetica", "normal");
    doc.text(
      `Expense ID : ${selected.expense_id || "-"}`,
      pageWidth - rightMargin,
      y,
      { align: "right" },
    );

    y += 8;

    // =========================
    // DATE + PURPOSE
    // =========================
    const expenseDate = selected.created_at
      ? new Date(selected.created_at).toLocaleDateString("en-GB")
      : "-";

    doc.setFontSize(10);

    doc.text(`Date : ${expenseDate}`, pageWidth - rightMargin, y, {
      align: "right",
    });

    const purposeWidth = 110;

    const purposeLines = doc.splitTextToSize(
      `Purpose: ${selected?.purpose || "-"}`,
      purposeWidth,
    );

    doc.text(purposeLines, leftMargin, y);

    y += purposeLines.length * 4 + 6;

    // =========================
    // HEADER TABLE
    // =========================
    autoTable(doc, {
      startY: y,
      theme: "grid",

      margin: {
        left: leftMargin,
        right: rightMargin,
      },

      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },

      body: [
        [
          "Name",
          user?.name || "-",
          "Designation",
          user?.designation_name || "-",
        ],
        [
          "Department",
          user?.department_name || "-",
          "Location",
          user?.location_name || "-",
        ],
      ],

      columnStyles: {
        0: {
          cellWidth: 30,
          fillColor: [220, 230, 241],
          fontStyle: "bold",
        },
        1: {
          cellWidth: 69,
        },
        2: {
          cellWidth: 30,
          fillColor: [220, 230, 241],
          fontStyle: "bold",
        },
        3: {
          cellWidth: 55,
        },
      },
    });

    y = doc.lastAutoTable.finalY + 8;

    // =========================
    // TABLE DATA
    // =========================
    let serialNo = 1;
    const tableRows = [];
    const currencyTotals = {};

    Object.entries(groupedByCurrency).forEach(([currency, items]) => {
      const total = items.reduce(
        (sum, item) => sum + Number(item.total_amount || 0),
        0,
      );

      const approvedTotal = items.reduce(
        (sum, item) => sum + Number(item.approved_amount || 0),
        0,
      );

      currencyTotals[currency] = approvedTotal;

      // -------------------------
      // EXPENSE ROWS
      // -------------------------
      items.forEach((item) => {
        tableRows.push([
          serialNo++,
          item.date ? new Date(item.date).toLocaleDateString("en-GB") : "-",
          item.description || "-",
          item.expense_type_name || "-",
          currency,
          formatCurrencyAmount(Number(item.total_amount || 0), currency),
          item.approved_amount
            ? formatCurrencyAmount(Number(item.approved_amount), currency)
            : "-",
        ]);
      });

      // -------------------------
      // AMOUNT IN WORDS
      // -------------------------
      const amountInWords = toWords(Math.round(approvedTotal)).replace(
        /\b\w/g,
        (char) => char.toUpperCase(),
      );

      tableRows.push([
        {
          content: `Amount In Words : (${currency}) ${amountInWords} Only`,
          colSpan: 5,
          styles: {
            fillColor: [253, 233, 217],
            halign: "left",
            fontStyle: "normal",
            overflow: "linebreak",
            valign: "middle",
            cellPadding: 3,
          },
        },
        {
          content: formatCurrencyAmount(total, currency),
          styles: {
            fillColor: [253, 233, 217],
            fontStyle: "bold",
            halign: "right",
          },
        },
        {
          content: formatCurrencyAmount(approvedTotal, currency),
          styles: {
            fillColor: [253, 233, 217],
            fontStyle: "bold",
            halign: "right",
          },
        },
      ]);
    });

    // =========================
    // MAIN EXPENSE TABLE
    // =========================
    autoTable(doc, {
      startY: y,
      theme: "grid",

      margin: {
        left: leftMargin,
        right: rightMargin,
      },

      head: [
        [
          "SNO",
          "DATE",
          "DESCRIPTION",
          "TYPE",
          "CURRENCY",
          "AMOUNT",
          "APPROVED AMOUNT",
        ],
      ],

      body: tableRows,

      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
        overflow: "linebreak",
      },

      headStyles: {
        fillColor: [220, 230, 241],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        font: "helvetica",
      },

      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 18, halign: "center" },
        2: { cellWidth: 50, halign: "left" },
        3: { cellWidth: 27, halign: "left" },
        4: { cellWidth: 22, halign: "center" },
        5: { cellWidth: 28.5, halign: "right" },
        6: { cellWidth: 28.5, halign: "right" },
      },
    });

    // =========================
    // TOTAL - AFTER EXPENSE TABLE
    // =========================
    let totalY = doc.lastAutoTable.finalY + 8;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const currencyEntries = Object.entries(currencyTotals);

    const totalsText =
      "Total :   " +
      currencyEntries
        .map(
          ([currency, amount]) =>
            `${currency} ${formatCurrencyAmount(amount, currency)}`,
        )
        .join("   |   ");

    doc.text(totalsText, leftMargin, totalY);

    // =========================
    // APPROVAL DATA
    // =========================
    const approvalSteps = (selected?.approvals || [])
      .slice()
      .sort((a, b) => (a.serial_no || 0) - (b.serial_no || 0));

    // =========================
    // DATE & TIME FORMAT
    // =========================

    const formatApprovalDateTime = (dateString) => {
      if (!dateString) return "-";

      // Treat timezone-less API timestamps as UTC.
      const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(dateString);

      const date = new Date(hasTimezone ? dateString : `${dateString}Z`);

      if (isNaN(date.getTime())) return "-";

      const formattedDate = date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });

      const formattedTime = date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });

      return `${formattedDate} | ${formattedTime}`;
    };

    // =========================
    // STATUS COLOR
    // =========================
    const getStatusColor = (status) => {
      const currentStatus = (status || "pending").toLowerCase();
    };

    // =========================
    // APPROVAL SECTION HEIGHT
    // =========================
    const stepsPerRow = 4;
    const approvalRows = Math.ceil(approvalSteps.length / stepsPerRow);

    // Approximate height required
    const approvalSectionHeight =
      approvalSteps.length > 0 ? 8 + approvalRows * 28 + 10 : 0;

    // Submitted By section
    const submittedSectionHeight = 10;

    // =========================
    // CHECK AVAILABLE SPACE
    // =========================
    const requiredBottomSpace =
      approvalSectionHeight + submittedSectionHeight + bottomMargin;

    // If Total + bottom section overlap,
    // move approval section to new page
    if (totalY + requiredBottomSpace > pageHeight) {
      doc.addPage();
    }

    // =========================
    // APPROVAL STATUS
    // ALWAYS AT BOTTOM
    // =========================
    let submittedY;
    let approvalY;

    if (approvalSteps.length > 0) {
      // Calculate from BOTTOM of page
      submittedY = pageHeight - bottomMargin;

      const approvalTableTotalHeight = approvalRows * 28;

      approvalY =
        submittedY - submittedSectionHeight - approvalTableTotalHeight - 8;

      // TITLE
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);

      doc.text("APPROVAL STATUS", leftMargin, approvalY);

      approvalY += 5;

      // =========================
      // CREATE APPROVAL TABLES
      // =========================
      for (let i = 0; i < approvalSteps.length; i += stepsPerRow) {
        const chunk = approvalSteps.slice(i, i + stepsPerRow);

        // APPROVAL LEVEL
        const headerRow = chunk.map((step) =>
          step.level_name?.toLowerCase() === "initiated by"
            ? "Created By"
            : step.level_name || "Approver",
        );

        // NAME + STATUS
        const nameStatusRow = chunk.map((step) => {
          const approverUser = getUserDetails(step.user_id);

          const name = approverUser?.name || step.username || "-";

          const status = (step.status || "Pending").toUpperCase();

          return {
            content: `${name} | ${status}`,
            styles: {
              fillColor: getStatusColor(step.status),
              fontStyle: "bold",
              halign: "center",
              valign: "middle",
            },
          };
        });

        // DATE + TIME
        const dateTimeRow = chunk.map((step) => {
          const isCreatedBy =
            step.level_name?.toLowerCase() === "initiated by" ||
            Number(step.serial_no) === 1;

          const approvalDate = isCreatedBy
            ? selected.created_at
            : step.approved_at;
          return formatApprovalDateTime(approvalDate);
        });

        // APPROVAL TABLE
        autoTable(doc, {
          startY: approvalY,
          theme: "grid",

          margin: {
            left: leftMargin,
            right: rightMargin,
          },

          head: [headerRow],
          body: [nameStatusRow, dateTimeRow],

          styles: {
            font: "helvetica",
            fontSize: 8,
            cellPadding: 3,
            lineWidth: 0.3,
            lineColor: [0, 0, 0],
            textColor: [0, 0, 0],
            halign: "center",
            valign: "middle",
          },

          headStyles: {
            fillColor: [220, 230, 241],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            halign: "center",
            valign: "middle",
          },

          bodyStyles: {
            fillColor: [255, 255, 255],
          },

          columnStyles: chunk.reduce((styles, _, index) => {
            styles[index] = {
              cellWidth: (pageWidth - leftMargin - rightMargin) / chunk.length,
            };

            return styles;
          }, {}),

          didParseCell: function (data) {
            if (data.section === "body" && data.row.index === 1) {
              data.cell.styles.fillColor = [255, 255, 255];

              data.cell.styles.fontStyle = "normal";
            }
          },
        });

        approvalY = doc.lastAutoTable.finalY + 5;
      }

      // Use actual table position
      submittedY = doc.lastAutoTable.finalY + 8;
    } else {
      // If no approval exists,
      // Submitted By stays at bottom
      submittedY = pageHeight - bottomMargin;
    }

    // =========================
    // SUBMITTED BY + DATE
    // =========================
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    doc.text(`Submitted By: ${user?.name || "-"}`, leftMargin, submittedY);

    doc.text(`Date: ${expenseDate}`, pageWidth - rightMargin, submittedY, {
      align: "right",
    });

    // =========================
    // DOWNLOAD PDF
    // =========================
    const fileName = `${selected.expense_id}.pdf`;

    doc.save(fileName);
  };
  const [attachmentLoading, setAttachmentLoading] = useState({});
  const getUserDetails = (id) => {
    if (!Array.isArray(users)) return null;
    return users.find((u) => String(u.id) === String(id));
  };

  // ✅ Returns the relevant approver name based on overall status
  const getStatusApproverName = (item) => {
    if (!Array.isArray(item?.approvals) || item.approvals.length === 0) {
      return null;
    }

    const status = item?.status?.toLowerCase();
    const sorted = [...item.approvals].sort(
      (a, b) => (a.serial_no ?? 0) - (b.serial_no ?? 0),
    );

    if (status === "pending") {
      // whoever's turn it currently is
      const pending = sorted.filter(
        (a) => a.status?.toLowerCase() === "pending",
      );
      return pending.length > 0 ? pending[0].username : null;
    }

    if (status === "rejected") {
      // the approver who actually rejected it
      const rejected = sorted.filter(
        (a) => a.status?.toLowerCase() === "rejected",
      );
      return rejected.length > 0 ? rejected[0].username : null;
    }

    if (status === "approved") {
      // the last approver in the chain (final sign-off)
      const approved = sorted.filter(
        (a) => a.status?.toLowerCase() === "approved",
      );
      return approved.length > 0
        ? approved[approved.length - 1].username
        : null;
    }

    return null;
  };
  // ✅ ADD THIS
  const getPendingApproverName = (item) => {
    if (!Array.isArray(item?.approvals) || item.approvals.length === 0) {
      return null;
    }

    // Find the pending approvals, sorted by serial_no
    const pending = item.approvals
      .filter((a) => a.status?.toLowerCase() === "pending")
      .sort((a, b) => (a.serial_no ?? 0) - (b.serial_no ?? 0));

    // The first one (lowest serial_no) is whose turn it currently is
    return pending.length > 0 ? pending[0].username : null;
  };
  const handleHeaderAttachment = async (download = false) => {
    try {
      if (!selected?.attachment) {
        toastError("No attachment found");
        return;
      }

      const attachment = selected.attachment;

      // Handle both string and object attachment formats
      let fileName = "";

      if (typeof attachment === "string") {
        fileName = attachment.split("/").pop();
      } else {
        fileName =
          attachment?.filename ||
          attachment?.file_name ||
          attachment?.name ||
          "";
      }

      if (!fileName) {
        toastError("Attachment file name not found");
        return;
      }

      const actionKey = `header-${download ? "download" : "view"}`;

      setAttachmentLoading((prev) => ({
        ...prev,
        [actionKey]: true,
      }));

      console.log("Attachment Debug:", {
        attachment,
        fileName,
        companyId,
        masterId: selected.id,
        expenseCode: selected.expense_id,
        download,
      });

      const res = await axios.get(`${API_URL}/expense/attachment`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          company_id: companyId,
          master_id: selected.id,
          expense_code: selected.expense_id,
          file_name: fileName,
          download,
        },
        responseType: "blob",
      });

      const fileURL = window.URL.createObjectURL(res.data);
      const mime = res.data.type || "";

      const isPdf =
        mime.includes("pdf") || fileName.toLowerCase().endsWith(".pdf");

      const isExcel =
        mime.includes("spreadsheet") ||
        mime.includes("excel") ||
        fileName.toLowerCase().endsWith(".xlsx") ||
        fileName.toLowerCase().endsWith(".xls");

      if (!download) {
        if (isPdf) {
          setPreviewType("pdf");
          setPreviewUrl(fileURL);
          setShowPreview(true);
        } else if (isExcel) {
          const arrayBuffer = await res.data.arrayBuffer();

          const workbook = XLSX.read(arrayBuffer, {
            type: "array",
          });

          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          const rows = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
          });

          setExcelData(rows);
          setPreviewType("excel");
          setShowPreview(true);
        } else {
          setPreviewType("image");
          setPreviewUrl(fileURL);
          setShowPreview(true);
        }
      } else {
        const link = document.createElement("a");

        link.href = fileURL;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      // Clean blob URL after a while
      setTimeout(() => {
        window.URL.revokeObjectURL(fileURL);
      }, 60000);
    } catch (err) {
      console.error("Attachment API Error:", err);

      // Important: blob response can contain backend JSON error
      if (err.response?.data instanceof Blob) {
        const errorText = await err.response.data.text();
        console.error("Backend error:", errorText);
      }

      showApiError(err);
      toastError("Failed to load header attachment");
    } finally {
      const actionKey = `header-${download ? "download" : "view"}`;

      setAttachmentLoading((prev) => ({
        ...prev,
        [actionKey]: false,
      }));
    }
  };

  const formatIndianDateTime = (dateString) => {
    if (!dateString) return "-";

    const utcDate = new Date(
      dateString.endsWith("Z") ? dateString : dateString + "Z",
    );

    return utcDate.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };
  const formatApprovalDateTime = (dateString) => {
    if (!dateString) return "-";

    // Treat timestamps without timezone information as UTC.
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(dateString);

    const date = new Date(hasTimezone ? dateString : `${dateString}Z`);

    if (isNaN(date.getTime())) return "-";

    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });

    const formattedTime = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });

    return `${formattedDate} | ${formattedTime}`;
  };
  const formatCurrencyAmount = useAuthStore((s) => s.formatCurrencyAmount);
  const sortedRequests = [...filteredRequests].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );
  const groupedRequests = sortedRequests.reduce((acc, item) => {
    const date = item.created_at?.split("T")[0] || "Unknown";

    if (!acc[date]) {
      acc[date] = [];
    }

    acc[date].push(item);
    return acc;
  }, {});

  const groupedByCurrency =
    selected?.expense_lines?.reduce((acc, item) => {
      const currency = item.currency || "NA";

      if (!acc[currency]) {
        acc[currency] = [];
      }

      acc[currency].push(item);
      return acc;
    }, {}) || {};

  // Replace the existing canEditExpense line with this function

  // ================= CAN EDIT EXPENSE =================
  const canEditExpense = () => {
    if (!selected) return false;

    // 1️⃣ Check if expense is pending
    if (selected.status?.toLowerCase() !== "pending") return false;

    // 2️⃣ Check if current user has edit permission (isedituser: 1)
    const currentUserId = useAuthStore.getState().user_id;
    const approvals = selected.approvals || [];

    const hasEditPermission = approvals.some(
      (a) =>
        Number(a.user_id) === Number(currentUserId) &&
        Number(a.isedituser) === 1,
    );

    console.log("🔍 canEditExpense (MyRequest):", {
      userId: currentUserId,
      status: selected.status,
      approvals: approvals.map((a) => ({
        user_id: a.user_id,
        username: a.username,
        isedituser: a.isedituser,
        matches: Number(a.user_id) === Number(currentUserId),
      })),
      hasEditPermission,
    });

    return hasEditPermission;
  };
  const canEditSendBackExpense = () => {
    if (!selected) return false;

    // Condition 1: Master expense status must be Pending
    if (selected.status?.toLowerCase() !== "pending") {
      return false;
    }

    // Condition 2: Expense must be sent back
    if (selected.is_send_back !== true) {
      return false;
    }

    const currentUserId = useAuthStore.getState().user_id;
    const approvals = selected.approvals || [];

    // Condition 3 & 4:
    // Current user must match an approval user_id.
    // DO NOT check approval status.
    // DO NOT check isedituser.
    const hasMatchingApprovalUser = approvals.some(
      (approval) => Number(approval.user_id) === Number(currentUserId),
    );

    return hasMatchingApprovalUser;
  };
  console.log("========== EDIT DEBUG ==========");
  console.log("Selected expense:", selected);
  console.log("Approvals JSON:", JSON.stringify(selected?.approvals, null, 2));
  console.log("Can edit:", canEditExpense);

  console.log(
    "Finance rows:",
    selected?.approvals?.filter(
      (approval) =>
        typeof approval.ta_type === "string" &&
        approval.ta_type.toLowerCase().includes("finance"),
    ),
  );

  const formatDateHeader = (dateString) => {
    if (!dateString || dateString === "Unknown") return dateString;

    const date = new Date(dateString + "T00:00:00");

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  console.log("================================");

  const showApiError = (err, fallback = "Something went wrong") => {
    // Axios network error
    if (err.code === "ERR_NETWORK") {
      toastError("Network error. Please check your internet connection.");
      return;
    }

    // Backend response message
    const message =
      err?.response?.data?.detail ||
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      fallback;

    toastError(message);
  };
  // ============================================================
  // CTRL + F STYLE GLOBAL SEARCH HIGHLIGHT
  // Works in both LEFT and RIGHT panels
  // ============================================================

  const clearSearchHighlights = () => {
    if (!window.CSS?.highlights) return;

    window.CSS.highlights.delete("global-search-highlight");
    window.CSS.highlights.delete("global-search-active");

    searchMatchesRef.current = [];
    activeSearchMatchRef.current = -1;
  };

  const applySearchHighlights = (searchText) => {
    clearSearchHighlights();

    if (!searchText?.trim()) {
      return;
    }

    if (!window.CSS?.highlights || !window.Highlight) {
      console.warn("CSS Custom Highlight API is not supported.");
      return;
    }

    const text = searchText.trim().toLowerCase();

    const containers = document.querySelectorAll(".left-panel, .right-panel");

    const matches = [];

    containers.forEach((container) => {
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            // Ignore empty text nodes
            if (!node.nodeValue?.trim()) {
              return NodeFilter.FILTER_REJECT;
            }

            // Ignore script/style content
            const parent = node.parentElement;

            if (
              parent &&
              ["SCRIPT", "STYLE", "NOSCRIPT"].includes(parent.tagName)
            ) {
              return NodeFilter.FILTER_REJECT;
            }

            return NodeFilter.FILTER_ACCEPT;
          },
        },
      );

      const textNodes = [];

      let node;

      while ((node = walker.nextNode())) {
        textNodes.push(node);
      }

      textNodes.forEach((textNode) => {
        const nodeText = textNode.nodeValue || "";
        const lowerText = nodeText.toLowerCase();

        let startIndex = 0;

        while (startIndex < lowerText.length) {
          const matchIndex = lowerText.indexOf(text, startIndex);

          if (matchIndex === -1) {
            break;
          }

          const range = new Range();

          range.setStart(textNode, matchIndex);
          range.setEnd(textNode, matchIndex + text.length);

          matches.push(range);

          startIndex = matchIndex + text.length;
        }
      });
    });

    searchMatchesRef.current = matches;

    if (!matches.length) {
      return;
    }

    const highlight = new window.Highlight(...matches);

    window.CSS.highlights.set("global-search-highlight", highlight);

    // First match is active
    activeSearchMatchRef.current = 0;
    const activeHighlight = new window.Highlight(matches[0]);

    window.CSS.highlights.set("global-search-active", activeHighlight);
  };
  const goToSearchMatch = (direction = 1) => {
    const matches = searchMatchesRef.current;

    if (!matches.length) {
      return;
    }

    let currentIndex = activeSearchMatchRef.current;

    currentIndex += direction;

    if (currentIndex >= matches.length) {
      currentIndex = 0;
    }

    if (currentIndex < 0) {
      currentIndex = matches.length - 1;
    }

    activeSearchMatchRef.current = currentIndex;

    if (window.CSS?.highlights && window.Highlight) {
      window.CSS.highlights.delete("global-search-active");

      const activeHighlight = new window.Highlight(matches[currentIndex]);

      window.CSS.highlights.set("global-search-active", activeHighlight);
    }

    // Scroll selected match into view
    const range = matches[currentIndex];

    const rect = range.getBoundingClientRect();

    const element = range.startContainer.parentElement;

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (rect) {
      window.scrollTo({
        top: window.scrollY + rect.top - window.innerHeight / 2,
        behavior: "smooth",
      });
    }
  };
  const handleGlobalSearch = (filters) => {
    let filtered = [...requests];

    const searchText = filters?.name?.trim().toLowerCase() || "";

    // ============================================================
    // EXPENSE TYPE DISPLAY NAME
    // ============================================================
    const expenseTypeNameMap = {
      EC: "Expense Claim",
      PT: "Petty Cash",
      BT: " Advance Request",
      ECAA: "Advance Settlement",
      RAA: "Advance Refund",
    };

    // ============================================================
    // GLOBAL TEXT SEARCH
    // ============================================================
    if (searchText) {
      filtered = filtered.filter((item) => {
        const user = users.find((u) => Number(u.id) === Number(item.user_id));

        // --------------------------------------------------------
        // MAIN EXPENSE / CREATED BY DATA
        // --------------------------------------------------------
        const mainExpenseValues = [
          // Expense type
          item.type,
          expenseTypeNameMap[item.type],

          // Expense
          item.expense_id,
          item.purpose,
          item.description,
          item.status,
          item.currency,
          item.employee_code,
          item.user_id,
          item.total_amount,

          // Created By
          user?.name,
          user?.username,
          user?.full_name,
          user?.employee_code,
          user?.department_name,
          user?.department,
          user?.designation_name,
          user?.designation,
          user?.location_name,
          user?.location,

          // Direct expense fields
          item.department_name,
          item.designation_name,
          item.location_name,
        ];

        // --------------------------------------------------------
        // APPROVAL / STATUS TAB DATA
        // --------------------------------------------------------
        const approvalValues = (item.approvals || []).flatMap((approval) => {
          const approvalUser = users.find(
            (u) => Number(u.id) === Number(approval.user_id),
          );

          return [
            // Approval details
            approval.username,
            approval.user_id,
            approval.status,
            approval.level_name,
            approval.remarks,
            approval.remark,
            approval.ta_type,
            approval.serial_no,
            approval.approved_at,

            // Approval user's details
            approval.department_name,
            approval.designation_name,
            approval.location_name,
            approval.employee_code,

            // From users API
            approvalUser?.name,
            approvalUser?.username,
            approvalUser?.full_name,
            approvalUser?.employee_code,
            approvalUser?.department_name,
            approvalUser?.department,
            approvalUser?.designation_name,
            approvalUser?.designation,
            approvalUser?.location_name,
            approvalUser?.location,
          ];
        });

        // --------------------------------------------------------
        // LINE ITEM DATA
        // --------------------------------------------------------
        const lineValues = (item.expense_lines || []).flatMap((line) => [
          line.description,
          line.currency,
          line.expense_type_name,
          line.expense_type,
          line.expense_type_id,
          line.gl_code,
          line.cost_center,
          line.total_amount,
          line.approved_amount,
          line.remarks,
          line.remark,
          line.date,
        ]);

        // --------------------------------------------------------
        // REFERENCE DATA
        // --------------------------------------------------------
        const referenceValues = (item.references || []).flatMap((reference) => [
          reference.reference_expense_code,
          reference.reference_total_amount,
          reference.reference_status,
        ]);

        // Combine EVERYTHING
        const searchableValues = [
          ...mainExpenseValues,
          ...approvalValues,
          ...lineValues,
          ...referenceValues,
        ];

        return searchableValues.some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(searchText),
        );
      });
    }

    // ============================================================
    // CREATED DATE - FROM
    // ============================================================
    if (filters?.fromDate) {
      const fromDate = new Date(`${filters.fromDate}T00:00:00`);

      filtered = filtered.filter((item) => {
        if (!item.created_at) return false;

        const createdDate = new Date(item.created_at);

        return createdDate >= fromDate;
      });
    }

    // ============================================================
    // CREATED DATE - TO
    // ============================================================
    if (filters?.toDate) {
      const toDate = new Date(`${filters.toDate}T23:59:59`);

      filtered = filtered.filter((item) => {
        if (!item.created_at) return false;

        const createdDate = new Date(item.created_at);

        return createdDate <= toDate;
      });
    }

    // ============================================================
    // UPDATE LEFT PANEL
    // ============================================================
    setFilteredRequests(filtered);

    // ============================================================
    // UPDATE RIGHT PANEL
    //
    // If search result contains selected expense -> keep it.
    //
    // If current selected expense is NOT in search result ->
    // automatically open the first matching expense.
    // ============================================================
    if (filtered.length === 0) {
      setSelected(null);
      setShowDetail(false);
      return;
    }

    const selectedStillExists = selected
      ? filtered.some((item) => Number(item.id) === Number(selected.id))
      : false;

    if (!selectedStillExists) {
      setSelected(filtered[0]);
      setShowDetail(true);
    }
  };
  // const generateIntegrationToken = useAuthStore(
  //   (s) => s.generateIntegrationToken,
  // );
  const [activeTab, setActiveTab] = useState("details");

  useEffect(() => {
    const fetchData = async () => {
      if (!userId || !companyId || !token) return;

      // Clear old data immediately when changing menu
      setRequests([]);
      setFilteredRequests([]);
      setSelected(null);
      setShowDetail(false);

      try {
        setLoading(true);

        // Dashboard API
        const dashboardRes = await axios.get(
          `${API_URL}/dashboard/counts/${companyId}/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = dashboardRes.data?.data;

        const requestData = data?.my_requests;
        const approvalData = data?.my_approvals;

        setRequestCounts({
          all: requestData?.total || 0,
          pending: requestData?.pending || 0,
          approved: requestData?.approved || 0,
          rejected: requestData?.rejected || 0,
          withdraw: requestData?.withdrawn || 0,
        });

        setAuthCounts({
          all: approvalData?.total || 0,
          pending: approvalData?.pending || 0,
          approved: approvalData?.approved || 0,
          rejected: approvalData?.rejected || 0,
        });

        // Get IDs based on selected menu
        let ids = [];

        if (status === "pending") {
          ids = requestData?.pending_ids || [];
        } else if (status === "approved") {
          ids = requestData?.approved_ids || [];
        } else if (status === "rejected") {
          ids = requestData?.rejected_ids || [];
        } else if (status === "withdraw") {
          ids = requestData?.withdrawn_ids || [];
        } else {
          // All
          ids = [
            ...(requestData?.pending_ids || []),
            ...(requestData?.approved_ids || []),
            ...(requestData?.rejected_ids || []),
            ...(requestData?.withdrawn_ids || []),
          ];
        }

        // No IDs = definitely show empty page
        if (!ids.length) {
          setRequests([]);
          setFilteredRequests([]);
          setSelected(null);
          setShowDetail(false);
          return;
        }

        // Expense API
        const idsString = ids.join(",");

        const expenseRes = await axios.get(
          `${API_URL}/expense/${companyId}/by-ids?ids=${idsString}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const expenses = expenseRes.data?.data || [];

        setRequests(expenses);
        setFilteredRequests(expenses);
      } catch (err) {
        showApiError(err);
        setRequests([]);
        setFilteredRequests([]);
        setSelected(null);
        setShowDetail(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    userId,
    companyId,
    token,
    status,
    setRequestCounts,
    setAuthCounts,
    location.state,
  ]);
  const handleWithdraw = async () => {
    if (!selected?.id) {
      toastError("Expense not found.");
      return;
    }

    try {
      setLoading(true);

      await axios.patch(
        `${API_URL}/expense/${companyId}/withdraw/${selected.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      toastSuccess("Expense withdrawn successfully.");

      setRequests((prev) =>
        prev.filter((item) => Number(item.id) !== Number(selected.id)),
      );

      setFilteredRequests((prev) =>
        prev.filter((item) => Number(item.id) !== Number(selected.id)),
      );

      setSelected(null);
      setShowDetail(false);
    } catch (err) {
      console.error("Withdraw API Error:", err);
      showApiError(err, "Failed to withdraw expense.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const initUsers = async () => {
      const store = useAuthStore.getState();

      await store.getUsers();
    };

    if (!users.length) {
      initUsers();
    }
  }, [users.length]);
  useEffect(() => {
    const handleSearchEnter = (event) => {
      const direction = event.detail?.direction || 1;

      goToSearchMatch(direction);
    };

    window.addEventListener("global-search-enter", handleSearchEnter);

    return () => {
      window.removeEventListener("global-search-enter", handleSearchEnter);
    };
  }, []);
  // ✅ Search effect - runs when searchFilters or requests change
  useEffect(() => {
    handleGlobalSearch(searchFilters);
  }, [searchFilters, requests]);
  useEffect(() => {
    const searchText = searchFilters?.name?.trim() || "";

    // Wait until React has rendered the filtered
    // left/right panels.
    const timer = setTimeout(() => {
      applySearchHighlights(searchText);
    }, 0);

    return () => {
      clearTimeout(timer);
      clearSearchHighlights();
    };
  }, [searchFilters?.name, filteredRequests, selected, activeTab]);

  const [previewType, setPreviewType] = useState(null); // "image" | "pdf"

  const [excelData, setExcelData] = useState(null); // array of rows

  const handleAttachment = async (item, download = false) => {
    try {
      const expense = selected;
      const fileName = item.attachment.split("/").pop();
      const actionKey = `${item.id}-${download ? "download" : "view"}`;

      setAttachmentLoading((prev) => ({ ...prev, [actionKey]: true }));

      const res = await axios.get(`${API_URL}/expense/attachment`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          company_id: companyId,
          master_id: expense.id,
          expense_code: expense.expense_id,
          file_name: fileName,
          download: download,
        },
        responseType: "blob",
      });

      const fileURL = window.URL.createObjectURL(res.data);
      const mime = res.data.type;
      const isPdf = mime.includes("pdf");
      const isExcel =
        mime.includes("spreadsheet") ||
        mime.includes("excel") ||
        fileName.toLowerCase().endsWith(".xlsx") ||
        fileName.toLowerCase().endsWith(".xls");

      if (!download) {
        if (isPdf) {
          setPreviewType("pdf");
          setPreviewUrl(fileURL);
          setShowPreview(true);
        } else if (isExcel) {
          const arrayBuffer = await res.data.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          setExcelData(rows);
          setPreviewType("excel");
          setShowPreview(true);
        } else {
          setPreviewType("image");
          setPreviewUrl(fileURL);
          setShowPreview(true);
        }
        return;
      }

      // download branch unchanged
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      showApiError(err);
      toastError("Failed to load attachment");
    } finally {
      const actionKey = `${item.id}-${download ? "download" : "view"}`;
      setAttachmentLoading((prev) => ({ ...prev, [actionKey]: false }));
    }
  };
  const expenseTypeNameMap = {
    EC: "Expense Claim",
    PT: " Petty Cash",
    BT: " Advance Request",
    ECAA: "Advance Settlement",
    RAA: "Advance Refund",
  };
  const canWithdrawExpense = () => {
    if (!selected) return false;

    // Withdraw button should be available only in Pending and All menus
    if (!["pending", "all"].includes(status?.toLowerCase())) {
      return false;
    }

    // Master/header expense status must be Pending
    if (selected.status?.toLowerCase() !== "pending") {
      return false;
    }

    // Current logged-in user must be the same as the master expense user
    if (Number(userId) !== Number(selected.user_id)) {
      return false;
    }

    return true;
  };
  if (loading) {
    return (
      <div className="grade-limit-container">
        <div className="center-loader">
          <FaSpinner className="spinner" />
          <div>Loading...</div>
        </div>
      </div>
    );
  }
  return (
    <div className="container-fluid">
      <div className="row">
        {/* ================= LEFT PANEL ================= */}
        <div
          className={`col-12 col-md-5 left-panel ${
            showDetail ? "hide-mobile" : ""
          }`}
        >
          {Object.keys(groupedRequests).length === 0 ? (
            <div className="no-data-found">No Data Found</div>
          ) : (
            Object.entries(groupedRequests)
              .sort((a, b) => new Date(b[0]) - new Date(a[0]))
              .map(([date, items]) => (
                <div key={date}>
                  {/* DATE HEADER */}
                  <div className="date-header">
                    {formatDateHeader(date)}
                  </div>{" "}
                  <div className="card-list">
                    {items.map((item) => {
                      const user = getUserDetails(item.user_id);

                      const name = user?.name || "N/A";
                      const dept = user?.department_name || "N/A";
                      const empCode = item?.employee_code || "-";
                      const initials = name
                        .split(" ")
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join("")
                        .toUpperCase();

                      return (
                        <div
                          key={item.id}
                          className={`task-card ${
                            selected?.id === item.id ? "active-ticket" : ""
                          }`}
                          onClick={() => {
                            setSelected(item);
                            setShowDetail(true);
                          }}
                        >
                          {/* ROW 1: FULL-WIDTH EXPENSE TYPE + EXPENSE ID */}
                          <div className="expense-type-row">
                            <div className="expense-type-name">
                              {expenseTypeNameMap[item?.type] ||
                                item?.type ||
                                "-"}
                            </div>

                            <span className="sepp">|</span>

                            <span className="value-expense">
                              {item?.expense_id || "-"}
                            </span>
                          </div>

                          {/* ROW 2: LEFT CONTENT + RIGHT STATUS */}
                          <div className="card-container">
                            {/* LEFT SIDE */}
                            <div className="avatar">{initials}</div>

                            <div className="card-content">
                              {/* EMPLOYEE DETAILS */}
                              <div className="row1">
                                <span className="emp">{empCode}</span>
                                <span className="sep">|</span>
                                <span className="name">{name}</span>
                                <span className="sep">|</span>
                                <span className="dept">{dept}</span>
                              </div>

                              {/* PURPOSE */}
                              <div className="row2">
                                {item.purpose || "No Purpose"}
                              </div>

                              {/* AMOUNT */}
                              <div className="row3">
                                {Object.entries(
                                  item.expense_lines?.reduce((acc, line) => {
                                    const currency =
                                      line.currency || item.currency || "NA";

                                    if (!acc[currency]) {
                                      acc[currency] = 0;
                                    }

                                    acc[currency] += Number(
                                      line.total_amount || 0,
                                    );
                                    return acc;
                                  }, {}) || {},
                                ).map(([currency, total], index, arr) => (
                                  <div
                                    key={currency}
                                    className="currency-wrapper"
                                  >
                                    <div className="currency-chip">
                                      <span className="currency-code">
                                        {currency}
                                      </span>
                                      <span className="currency-value">
                                        {formatCurrencyAmount(total, currency)}
                                      </span>
                                    </div>

                                    {index !== arr.length - 1 && (
                                      <span className="currency-separator">
                                        |
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* RIGHT SIDE STATUS */}
                            <div className="card-right">
                              <div
                                className={`status-badge ${item.status?.toLowerCase()}`}
                              >
                                {item.status}
                              </div>

                              {["pending", "approved", "rejected"].includes(
                                item.status?.toLowerCase(),
                              ) && (
                                <div
                                  className={`status-approver-name ${item.status?.toLowerCase()}`}
                                >
                                  {getStatusApproverName(item) || "-"}
                                </div>
                              )}
                              {item?.is_send_back === true && (
                                <div className="send-back-status">
                                  <span className="send-back-icon">
                                    <LuCornerUpLeft />
                                  </span>
                                  <span>Sent Back</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
          )}
        </div>

        <div
          className={`col-12 col-md-7 right-panel ${
            showDetail ? "show-mobile" : "hide-mobile"
          }`}
        >
          {selected &&
            (() => {
              const user = getUserDetails(selected.user_id);

              const grade = getGradeDetails(selected.user_grade);

              return (
                <>
                  {showDetail && (
                    <div
                      className="mobile-back"
                      onClick={() => setShowDetail(false)}
                    >
                      <button className="mobile-back-btn">
                        <img src={backIcon} alt="back" className="back-icon" />
                      </button>
                    </div>
                  )}
                  {/* ================= HEADER ================= */}
                  <div className="expense-header">
                    <div className="my-req-header-top">
                      <div className="header-user-info">
                        {expenseTypeNameMap[selected?.type] ||
                          selected?.type ||
                          "-"}
                        <span>|</span>
                        {user?.name || "N/A"} <span>|</span>
                        {user?.department_name || "N/A"} <span>|</span>
                        {selected?.employee_code || "-"}
                      </div>
                      {/* HEADER ATTACHMENT ACTIONS - RIGHT CORNER */}
                      {selected?.attachment && (
                        <div className="header-attachment-actions">
                          {attachmentLoading["header-view"] ? (
                            <div className="my-req-mini-loader"></div>
                          ) : (
                            <Eye
                              size={24}
                              strokeWidth={1.6}
                              className="my-req-attachment-icon"
                              title="Preview attachment"
                              onClick={() => handleHeaderAttachment(false)}
                            />
                          )}

                          {attachmentLoading["header-download"] ? (
                            <div className="my-req-mini-loader"></div>
                          ) : (
                            <Download
                              size={24}
                              strokeWidth={1.6}
                              className="my-req-attachment-icon"
                              title="Download attachment"
                              onClick={() => handleHeaderAttachment(true)}
                            />
                          )}
                        </div>
                      )}
                    </div>

                    {/* <div className="header-sub">
                      <span
                        className={`status-pill ${selected.status?.toLowerCase()}`}
                      >
                        {selected.status}
                      </span>

                      {["pending", "approved", "rejected"].includes(
                        selected.status?.toLowerCase(),
                      ) && (
                        <span
                          className={`status-approver-name ${selected.status?.toLowerCase()}`}
                        >
                          {selected.status?.toLowerCase() === "pending" &&
                            "Waiting on: "}
                          {selected.status?.toLowerCase() === "approved" &&
                            "Approved by: "}
                          {selected.status?.toLowerCase() === "rejected" &&
                            "Rejected by: "}
                          {getStatusApproverName(selected) || "-"}
                        </span>
                      )}
                    </div> */}
                    <div className="header-sub">
                      <span
                        className={`status-pill ${selected.status?.toLowerCase()}`}
                      >
                        {selected.status}
                      </span>

                      {["pending", "approved", "rejected"].includes(
                        selected.status?.toLowerCase(),
                      ) && (
                        <span
                          className={`status-approver-name ${selected.status?.toLowerCase()}`}
                        >
                          {selected.status?.toLowerCase() === "pending" &&
                            "Waiting on: "}
                          {selected.status?.toLowerCase() === "approved" &&
                            "Approved by: "}
                          {selected.status?.toLowerCase() === "rejected" &&
                            "Rejected by: "}
                          {getStatusApproverName(selected) || "-"}
                        </span>
                      )}
                    </div>
                    {selected.status?.toLowerCase() === "approved" && (
                      <div className="approved-instruction">
                        Print the approved form from iWorkkz, attach all
                        original bills/receipts, and submit the hard copies to
                        the Finance Team for payment process.
                      </div>
                    )}
                  </div>

                  <div className="tabs-header">
                    <div className="tabs">
                      <div
                        className={`tab ${activeTab === "details" ? "active" : ""}`}
                        onClick={() => setActiveTab("details")}
                      >
                        📄 Details
                      </div>

                      <div
                        className={`tab ${activeTab === "lineItems" ? "active" : ""}`}
                        onClick={() => setActiveTab("lineItems")}
                      >
                        🧾 Line Items ({selected?.expense_lines?.length || 0})
                      </div>

                      <div
                        className={`tab ${activeTab === "workflow" ? "active" : ""}`}
                        onClick={() => setActiveTab("workflow")}
                      >
                        🔄 Status ({selected?.approvals?.length || 0})
                      </div>
                    </div>

                    <div>
                      {canEditExpense() && (
                        <button
                          className="edit-btn"
                          onClick={() => {
                            navigate(`/edit-expense/${selected.id}`, {
                              state: {
                                expenseData: selected,
                                selectedExpenseId: selected.id,
                              },
                            });
                          }}
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="edit-icon"
                          />
                        </button>
                      )}
                    </div>

                    {status?.toLowerCase() !== "withdraw" && (
                      <img
                        src={pdfIcon}
                        alt="Download PDF"
                        className="pdf-download-icon"
                        onClick={async () => {
                          try {
                            const res = await axios.get(
                              `${API_URL}/settings/company/${companyId}`,
                              {
                                headers: { Authorization: `Bearer ${token}` },
                              },
                            );

                            downloadExpensePdf(res.data);
                          } catch (err) {
                            toastError("Failed to load company details");
                          }
                        }}
                      />
                    )}
                  </div>

                  {activeTab === "details" && (
                    <div className="tab-content">
                      <h3 className="section-title">Expense Details</h3>

                      <div className="detail-grid">
                        <div className="label">Expense ID:</div>
                        <div className="value">{selected.expense_id}</div>
                        <div className="label">Purpose:</div>
                        <div className="value">{selected.purpose}</div>
                        <div className="label">Created On:</div>
                        <div className="value">
                          {formatIndianDateTime(selected.created_at)}
                        </div>
                        <div className="label">Created By:</div>
                        <div className="value">{user?.name}</div>
                        <div className="label">Status:</div>
                        <div className="value">{selected.status}</div>
                        <div className="label">Draft Entry:</div>
                        <div className="value">
                          {selected?.erp_docentry ?? "-"}
                        </div>
                        <div className="label">Amount:</div>
                        <div className="value multi-currency-total">
                          {Object.entries(
                            selected?.expense_lines?.reduce((acc, line) => {
                              const currency = line.currency || "NA";

                              if (!acc[currency]) {
                                acc[currency] = 0;
                              }

                              acc[currency] += Number(line.total_amount || 0);

                              return acc;
                            }, {}) || {},
                          ).map(([currency, total], index, arr) => (
                            <span key={currency}>
                              {currency} {formatCurrencyAmount(total, currency)}
                              {index !== arr.length - 1 && " | "}
                            </span>
                          ))}
                        </div>
                        <div className="label">Description:</div>
                        <div className="value">
                          {selected?.description || "-"}
                        </div>

                        <div className="label">Payment Ref Number:</div>
                        <div className="value">
                          {selected?.payment_ref_no || "-"}
                        </div>
                      </div>

                      <hr />

                      {/* ================= EMPLOYEE DETAILS ================= */}
                      <h3 className="section-title">Employee Details</h3>

                      <div className="detail-grid">
                        <div className="label">Name:</div>
                        <div className="value">{user?.name}</div>

                        <div className="label">Employee Code:</div>
                        <div className="value">
                          {selected?.employee_code || "-"}
                        </div>

                        <div className="label">Department:</div>
                        <div className="value">{user?.department_name}</div>

                        <div className="label">Designation:</div>
                        <div className="value">{user?.designation_name}</div>

                        <div className="label">Grade:</div>
                        <div className="value">
                          {grade ? `${grade.title}` : "-"}
                        </div>

                        <div className="label">Location:</div>
                        <div className="value">{user?.location_name}</div>
                      </div>
                    </div>
                  )}
                  {activeTab === "lineItems" && (
                    <div className="tab-content">
                      <h3 className="section-title">
                        Line Items ({selected?.expense_lines?.length || 0})
                      </h3>

                      {/* ================= REFERENCE DETAILS ================= */}
                      {selected?.references?.length > 0 && (
                        <div className="reference-section">
                          <div className="reference-table-wrapper">
                            <table className="reference-table">
                              <thead>
                                <tr>
                                  <th>Expense Code</th>
                                  <th>Total Amount</th>
                                  <th>Reference Status</th>
                                </tr>
                              </thead>

                              <tbody>
                                {selected.references.map((reference) => (
                                  <tr key={reference.id}>
                                    <td>
                                      {reference.reference_expense_code || "-"}
                                    </td>

                                    <td>
                                      {formatCurrencyAmount(
                                        Number(
                                          reference.reference_total_amount || 0,
                                        ),
                                        selected?.currency || "INR",
                                      )}
                                    </td>

                                    <td>
                                      <span
                                        className={`reference-status ${reference.reference_status?.toLowerCase()}`}
                                      >
                                        {reference.reference_status || "-"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      <div className="line-table">
                        {/* ================= FIXED HEADER ================= */}
                        <div className="line-item line-header">
                          <div className="line-number">#</div>

                          <div className="line-center">
                            <div className="line-top">
                              <div className="line-field">
                                <span>Category</span>
                              </div>
                              <div className="line-field">
                                <span>Description</span>
                              </div>
                            </div>

                            <div className="line-bottom">
                              <div className="line-field">
                                <span>Date</span>
                              </div>
                              <div className="line-field">
                                <span>Requested</span>
                              </div>
                              <div className="line-field">
                                <span>Approved</span>
                              </div>
                              <div className="line-field">
                                <span>Remark</span>
                              </div>
                            </div>
                          </div>

                          <div className="line-attachment">
                            <img src={attachmentIcon} alt="Attachment" />
                          </div>
                        </div>

                        {/* ================= DATA ROWS ================= */}
                        {(() => {
                          let serialNo = 1;

                          return Object.entries(groupedByCurrency).map(
                            ([currency, items]) => {
                              const total = items.reduce(
                                (sum, item) =>
                                  sum + Number(item.total_amount || 0),
                                0,
                              );
                              const totalApproved = items.reduce(
                                (sum, item) =>
                                  sum + Number(item.approved_amount || 0),
                                0,
                              );

                              return (
                                <div key={currency}>
                                  {items.map((item) => (
                                    <div key={item.id} className="line-item">
                                      {/* SERIAL NUMBER */}
                                      <div className="line-number">
                                        {serialNo++}
                                      </div>

                                      {/* CENTER CONTENT */}
                                      <div className="line-center">
                                        {/* TOP: Category | Description */}
                                        <div className="line-top">
                                          <div className="line-field">
                                            <strong
                                              title={
                                                item.expense_type_name || "N/A"
                                              }
                                            >
                                              {item.expense_type_name || "N/A"}
                                            </strong>
                                          </div>
                                          <div className="line-field">
                                            <strong
                                              title={item.description || "N/A"}
                                            >
                                              {item.description || "N/A"}
                                            </strong>
                                          </div>
                                        </div>

                                        {/* BOTTOM: Date | Requested | Approved | Remark */}
                                        <div className="line-bottom">
                                          <div className="line-field">
                                            <strong>
                                              {item.date
                                                ? new Date(
                                                    item.date,
                                                  ).toLocaleDateString("en-GB")
                                                : selected.created_at
                                                  ? new Date(
                                                      selected.created_at,
                                                    ).toLocaleDateString(
                                                      "en-GB",
                                                    )
                                                  : "N/A"}
                                            </strong>
                                          </div>

                                          <div className="line-field">
                                            <strong>
                                              {formatCurrencyAmount(
                                                item.total_amount,
                                                currency,
                                              )}
                                            </strong>
                                          </div>

                                          <div className="line-field">
                                            <strong
                                              style={{ color: "#059669" }}
                                            >
                                              {item.approved_amount ? (
                                                formatCurrencyAmount(
                                                  Number(
                                                    item.approved_amount,
                                                  ).toFixed(2),
                                                  currency,
                                                )
                                              ) : (
                                                <span className="my-req-text-muted">
                                                  -
                                                </span>
                                              )}
                                            </strong>
                                          </div>

                                          <div className="line-field">
                                            <strong
                                              title={
                                                item.remark ||
                                                item.remarks ||
                                                "-"
                                              }
                                            >
                                              {item.remark ||
                                                item.remarks ||
                                                "-"}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>

                                      {/* ATTACHMENT */}
                                      <div className="line-attachment">
                                        {item.attachment ? (
                                          <div className="my-req-attachment-actions">
                                            {attachmentLoading[
                                              `${item.id}-view`
                                            ] ? (
                                              <div className="my-req-mini-loader"></div>
                                            ) : (
                                              <Eye
                                                size={18}
                                                strokeWidth={1.5}
                                                className="my-req-attachment-icon"
                                                onClick={() =>
                                                  handleAttachment(item, false)
                                                }
                                              />
                                            )}
                                            {attachmentLoading[
                                              `${item.id}-download`
                                            ] ? (
                                              <div className="my-req-mini-loader"></div>
                                            ) : (
                                              <Download
                                                size={18}
                                                strokeWidth={1.5}
                                                className="my-req-attachment-icon"
                                                onClick={() =>
                                                  handleAttachment(item, true)
                                                }
                                              />
                                            )}
                                          </div>
                                        ) : (
                                          <span className="my-req-text-muted">
                                            N/A
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}

                                  {/* CURRENCY SUBTOTAL */}
                                  <div className="currency-total-simple">
                                    <span className="total-title">
                                      Total ({currency})
                                    </span>
                                    <span className="total-amount">
                                      Requested:{" "}
                                      {formatCurrencyAmount(total, currency)}
                                    </span>
                                    <span className="total-separator">|</span>
                                    <span
                                      className="total-amount"
                                      style={{ color: "#059669" }}
                                    >
                                      Approved:{" "}
                                      {formatCurrencyAmount(
                                        totalApproved.toFixed(2),
                                        currency,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              );
                            },
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {activeTab === "workflow" && (
                    <div className="tab-content">
                      <h3 className="section-title">Status</h3>

                      <div className="timeline">
                        {(selected?.approvals || [])
                          .slice()
                          .sort((a, b) => a.serial_no - b.serial_no)
                          .map((step, index, arr) => {
                            const status = (step.status || "").toLowerCase();

                            // Find the rejected step
                            const rejectedIndex = arr.findIndex(
                              (item) =>
                                (item.status || "").toLowerCase() ===
                                "rejected",
                            );

                            // All steps after rejection should be greyed out
                            const isAfterRejected =
                              rejectedIndex !== -1 && index > rejectedIndex;

                            const getStatusUI = (status) => {
                              switch (status) {
                                case "pending":
                                  return {
                                    class: "pending",
                                    icon: pendingIcon,
                                  };

                                case "approved":
                                  return {
                                    class: "approved",
                                    icon: approvedIcon, // if you don’t have this, use closedIcon or checkIcon
                                  };

                                case "rejected":
                                  return {
                                    class: "rejected",
                                    icon: rejectedIcon, // cross icon recommended
                                  };

                                default:
                                  return {
                                    class: "pending",
                                    icon: pendingIcon,
                                  };
                              }
                            };
                            const ui = getStatusUI(status);

                            const name = step.username;
                            const dept = step.department_name;
                            const desig = step.designation_name;
                            const location = step.location_name;
                            // Approval date & time

                            const isCreatedBy =
                              step.level_name?.toLowerCase() ===
                                "initiated by" || Number(step.serial_no) === 1;

                            const approvalDate = isCreatedBy
                              ? selected.created_at
                              : step.approved_at;

                            return (
                              <div
                                className={`timeline-item ${
                                  isAfterRejected ? "timeline-disabled" : ""
                                }`}
                                key={step.id}
                              >
                                {" "}
                                {/* LEFT SIDE */}
                                <div className="timeline-left">
                                  <div
                                    className={`timeline-circle ${ui.class}`}
                                  >
                                    <img
                                      src={ui.icon}
                                      alt="status"
                                      className="timeline-icon"
                                    />
                                  </div>
                                </div>
                                <div className="timeline-content">
                                  <div className="wf-row wf-row-1">
                                    {step.level_name?.toLowerCase() ===
                                    "initiated by"
                                      ? "Created By"
                                      : step.level_name || `Step ${index + 1}`}
                                  </div>

                                  <div className="wf-row wf-row-2">
                                    <span>{name}</span>

                                    <span className="separator">|</span>

                                    <span className={`status ${ui.class}`}>
                                      {step.status}
                                    </span>

                                    <span className="separator">|</span>

                                    <span className="wf-date-time">
                                      {formatApprovalDateTime(approvalDate)}
                                    </span>

                                    {step.remarks && (
                                      <>
                                        <span className="separator">|</span>

                                        <span className="wf-remark">
                                          Remark: {step.remarks}
                                        </span>
                                      </>
                                    )}
                                  </div>

                                  {/* DETAILS */}
                                  <div className="wf-row wf-row-3">
                                    <span>{dept}</span>
                                    <span className="separator">|</span>
                                    <span>{desig}</span>
                                    <span className="separator">|</span>
                                    <span>{location}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  {(canWithdrawExpense() || canEditSendBackExpense()) && (
                    <div className="my-auth-sticky-actions">
                      <div className="my-auth-action-buttons my-request-withdraw-actions">
                        {/* EDIT BUTTON */}
                        {canEditSendBackExpense() && (
                          <button
                            type="button"
                            className="my-auth-edit-btn"
                            onClick={() => {
                              navigate(`/edit-expense/${selected.id}`, {
                                state: {
                                  expenseData: selected,
                                  selectedExpenseId: selected.id,
                                  sendBackEdit: true,
                                },
                              });
                            }}
                            title="Edit Expense"
                          >
                            Edit
                          </button>
                        )}

                        {/* WITHDRAW BUTTON */}
                        {canWithdrawExpense() && (
                          <button
                            type="button"
                            className="my-auth-withdraw-btn"
                            onClick={handleWithdraw}
                            disabled={loading}
                          >
                            {loading ? "Withdrawing..." : "Withdraw"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {showPreview && (
                    <div
                      className="preview-overlay"
                      onClick={() => setShowPreview(false)}
                    >
                      <div
                        className={
                          previewType === "pdf" || previewType === "excel"
                            ? "preview-box-pdf"
                            : "preview-box"
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="close-btn"
                          onClick={() => setShowPreview(false)}
                        >
                          ✕
                        </button>

                        {previewType === "pdf" && (
                          <iframe
                            src={previewUrl}
                            title="PDF Preview"
                            className="preview-pdf"
                          />
                        )}

                        {previewType === "excel" && (
                          <div className="excel-preview-wrapper">
                            <table className="excel-preview-table">
                              <tbody>
                                {excelData?.map((row, rIdx) => (
                                  <tr key={rIdx}>
                                    {row.map((cell, cIdx) => (
                                      <td key={cIdx}>{cell}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {previewType === "image" && (
                          <img
                            src={previewUrl}
                            alt="preview"
                            className="preview-img"
                          />
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
        </div>
      </div>
    </div>
  );
}
